import React, { useEffect, useRef, useState } from "react";

/**
 * Props:
 *  - onChange: (payload) => void
 *      payload = {
 *        mode: 'today'|'date'|'month'|'year'|'range',
 *        start: Date,
 *        end: Date
 *      }
 *  - initialMode?: same as mode above
 *  - initialStart?: Date
 *  - initialEnd?: Date
 *  - storageKey?: string    // 🔴 NEW: key for persisting state in sessionStorage
 */

const STORAGE_PREFIX = "tmDateFilter:";

/** Parse YYYY-MM-DD as a local calendar date (avoids UTC day-shift from ISO). */
function parseLocalYMD(s) {
  if (!s || typeof s !== "string") return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function toLocalYMD(d) {
  if (!d) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function loadStoredState(storageKey) {
  if (!storageKey) return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      ...parsed,
      selectedStart: parsed.selectedStart
        ? parseLocalYMD(parsed.selectedStart)
        : null,
      selectedEnd: parsed.selectedEnd
        ? parseLocalYMD(parsed.selectedEnd)
        : null,
    };
  } catch (e) {
    console.warn("[DateFilterBar] Failed to load stored date filter", e);
    return null;
  }
}

function saveStoredState(storageKey, state) {
  if (!storageKey) return;
  try {
    sessionStorage.setItem(
      STORAGE_PREFIX + storageKey,
      JSON.stringify({
        ...state,
        // Store local calendar dates, not UTC ISO (prevents off-by-one day)
        selectedStart: toLocalYMD(state.selectedStart),
        selectedEnd: toLocalYMD(state.selectedEnd),
      })
    );
  } catch (e) {
    console.warn("[DateFilterBar] Failed to save date filter", e);
  }
}

function clearStoredState(storageKey) {
  if (!storageKey) return;
  try {
    sessionStorage.removeItem(STORAGE_PREFIX + storageKey);
  } catch (e) {
    console.warn("[DateFilterBar] Failed to clear date filter", e);
  }
}

export default function DateFilterBar({
  onChange = () => {},
  initialMode = "today",
  initialStart,
  initialEnd,
  storageKey, // 🔴 NEW
}) {
  const wrapRef = useRef(null);

  const today = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const toDateStr = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const makeDayStart = (d) => {
    const c = new Date(d);
    c.setHours(0, 0, 0, 0);
    return c;
  };
  const makeDayEnd = (d) => {
    const c = new Date(d);
    c.setHours(23, 59, 59, 999);
    return c;
  };

  // Helpers to emit start/end as Date
  const parseYMD = (s) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const monthToRange = (yyyyMM) => {
    const [y, m] = yyyyMM.split("-").map(Number);
    const start = new Date(y, m - 1, 1);
    const end = new Date(y, m, 0); // last day of month
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  const yearToRange = (yyyyStr) => {
    const y = Number(yyyyStr);
    if (Number.isNaN(y)) return null;
    const start = new Date(y, 0, 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(y, 11, 31);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  // ---- Load stored state (if any) ----
  const stored =
    typeof window !== "undefined" ? loadStoredState(storageKey) : null;

  const defaultStart = makeDayStart(initialStart || today);
  const defaultEnd = makeDayEnd(initialEnd || today);

  // ---- internal inputs for the pickers ----
  const [mode, setMode] = useState(stored?.mode ?? initialMode);
  const [openMenu, setOpenMenu] = useState(null); // 'date'|'month'|'year'|'range'|null

  const [dateValue, setDateValue] = useState(
    stored?.dateValue ??
      (initialStart ? toDateStr(initialStart) : toDateStr(today))
  );
  const [monthValue, setMonthValue] = useState(
    stored?.monthValue ??
      (initialStart
        ? `${initialStart.getFullYear()}-${pad(initialStart.getMonth() + 1)}`
        : `${today.getFullYear()}-${pad(today.getMonth() + 1)}`)
  );
  const [yearValue, setYearValue] = useState(
    stored?.yearValue ??
      (initialStart ? String(initialStart.getFullYear()) : String(today.getFullYear()))
  );
  const [rangeStart, setRangeStart] = useState(
    stored?.rangeStart ??
      (initialStart ? toDateStr(initialStart) : toDateStr(today))
  );
  const [rangeEnd, setRangeEnd] = useState(
    stored?.rangeEnd ??
      (initialEnd ? toDateStr(initialEnd) : toDateStr(today))
  );

  // ---- “selected” state used for the summary display ----
  const [selectedMode, setSelectedMode] = useState(
    stored?.selectedMode ?? initialMode
  );
  const [selectedStart, setSelectedStart] = useState(
    stored?.selectedStart ?? defaultStart
  );
  const [selectedEnd, setSelectedEnd] = useState(
    stored?.selectedEnd ?? defaultEnd
  );

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const applyToday = () => {
    const start = makeDayStart(new Date());
    const end = makeDayEnd(new Date());
    setMode("today");
    setSelectedMode("today");
    setSelectedStart(start);
    setSelectedEnd(end);
    setOpenMenu(null);
    onChange({ mode: "today", start, end });
  };

  const applyDate = () => {
    const start = makeDayStart(parseYMD(dateValue));
    const end = makeDayEnd(start);
    setMode("date");
    setSelectedMode("date");
    setSelectedStart(start);
    setSelectedEnd(end);
    setOpenMenu(null);
    onChange({ mode: "date", start, end });
  };

  const applyMonth = () => {
    const { start, end } = monthToRange(monthValue);
    setMode("month");
    setSelectedMode("month");
    setSelectedStart(start);
    setSelectedEnd(end);
    setOpenMenu(null);
    onChange({ mode: "month", start, end });
  };

  const applyYear = () => {
    const range = yearToRange(yearValue);
    if (!range) {
      alert("Please enter a valid year.");
      return;
    }
    const { start, end } = range;
    setMode("year");
    setSelectedMode("year");
    setSelectedStart(start);
    setSelectedEnd(end);
    setOpenMenu(null);
    onChange({ mode: "year", start, end });
  };

  const applyRange = () => {
    const s = parseYMD(rangeStart);
    const e = parseYMD(rangeEnd);
    if (e < s) {
      alert("End date cannot be before start date.");
      return;
    }
    const start = makeDayStart(s);
    const end = makeDayEnd(e);
    setMode("range");
    setSelectedMode("range");
    setSelectedStart(start);
    setSelectedEnd(end);
    setOpenMenu(null);
    onChange({ mode: "range", start, end });
  };

  const Btn = ({ children, active, onClick }) => (
    <button
      onClick={onClick}
      className={`tm-btn ${active ? "tm-btn--active" : ""}`}
      type="button"
    >
      {children}
    </button>
  );

  // ---- summary text for “what's selected” ----
  const formatShort = (d) =>
    d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });

  const formatMonthYear = (d) =>
    d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
    });

  const getSummaryText = () => {
    if (!selectedStart || !selectedEnd) return "";

    switch (selectedMode) {
      case "today":
        return "Today";
      case "date":
        return formatShort(selectedStart);
      case "month":
        return formatMonthYear(selectedStart);
      case "year":
        return String(selectedStart.getFullYear());
      case "range":
        return `${formatShort(selectedStart)} – ${formatShort(selectedEnd)}`;
      default:
        return `${formatShort(selectedStart)} – ${formatShort(selectedEnd)}`;
    }
  };

  // 🔴 Persist whenever key pieces change
  useEffect(() => {
    saveStoredState(storageKey, {
      mode,
      dateValue,
      monthValue,
      yearValue,
      rangeStart,
      rangeEnd,
      selectedMode,
      selectedStart,
      selectedEnd,
    });
  }, [
    storageKey,
    mode,
    dateValue,
    monthValue,
    yearValue,
    rangeStart,
    rangeEnd,
    selectedMode,
    selectedStart,
    selectedEnd,
  ]);

  // 🔴 Clear stored state if component unmounts and you want a manual reset somewhere else
  // (optional – keep if you want external "Reset all" button to call clearStoredState)

  // 🔴 Fire onChange once on mount with whatever we restored
  useEffect(() => {
    if (!selectedStart || !selectedEnd) return;
    onChange({
      mode: selectedMode,
      start: selectedStart,
      end: selectedEnd,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only on mount

  return (
    <div ref={wrapRef} className="tm-filter-wrap">
      {openMenu && (
        <div
          className="tm-menu-backdrop"
          onClick={() => setOpenMenu(null)}
          aria-hidden
        />
      )}
      <div className="tm-label">Filter Data:</div>

      <div className="tm-row">
        {/* TODAY */}
        <Btn active={mode === "today"} onClick={applyToday}>
          Today
        </Btn>

        {/* DATE */}
        <div className="tm-pop">
          <Btn
            active={mode === "date"}
            onClick={() =>
              setOpenMenu(openMenu === "date" ? null : "date")
            }
          >
            Date
          </Btn>
          {openMenu === "date" && (
            <div className="tm-menu">
              <label className="tm-field">
                <span>Select date</span>
                <input
                  type="date"
                  value={dateValue}
                  onChange={(e) => setDateValue(e.target.value)}
                />
              </label>
              <div className="tm-actions">
                <button
                  type="button"
                  className="tm-apply"
                  onClick={applyDate}
                >
                  Apply
                </button>
                <button
                  type="button"
                  className="tm-cancel"
                  onClick={() => setOpenMenu(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* MONTH */}
        <div className="tm-pop">
          <Btn
            active={mode === "month"}
            onClick={() =>
              setOpenMenu(openMenu === "month" ? null : "month")
            }
          >
            Month
          </Btn>
          {openMenu === "month" && (
            <div className="tm-menu">
              <label className="tm-field">
                <span>Select month</span>
                <input
                  type="month"
                  value={monthValue}
                  onChange={(e) => setMonthValue(e.target.value)}
                />
              </label>
              <div className="tm-actions">
                <button
                  type="button"
                  className="tm-apply"
                  onClick={applyMonth}
                >
                  Apply
                </button>
                <button
                  type="button"
                  className="tm-cancel"
                  onClick={() => setOpenMenu(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* YEAR */}
        <div className="tm-pop">
          <Btn
            active={mode === "year"}
            onClick={() =>
              setOpenMenu(openMenu === "year" ? null : "year")
            }
          >
            Year
          </Btn>
          {openMenu === "year" && (
            <div className="tm-menu">
              <label className="tm-field">
                <span>Select year</span>
                <input
                  type="number"
                  value={yearValue}
                  onChange={(e) => setYearValue(e.target.value)}
                  min="2000"
                  max="2100"
                  step="1"
                />
              </label>
              <div className="tm-actions">
                <button
                  type="button"
                  className="tm-apply"
                  onClick={applyYear}
                >
                  Apply
                </button>
                <button
                  type="button"
                  className="tm-cancel"
                  onClick={() => setOpenMenu(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* RANGE */}
        <div className="tm-pop">
          <Btn
            active={mode === "range"}
            onClick={() =>
              setOpenMenu(openMenu === "range" ? null : "range")
            }
          >
            Custom Range
          </Btn>
          {openMenu === "range" && (
            <div className="tm-menu">
              <div className="tm-field-grid">
                <label className="tm-field">
                  <span>Start</span>
                  <input
                    type="date"
                    value={rangeStart}
                    max={rangeEnd}
                    onChange={(e) => setRangeStart(e.target.value)}
                  />
                </label>
                <label className="tm-field">
                  <span>End</span>
                  <input
                    type="date"
                    value={rangeEnd}
                    min={rangeStart}
                    onChange={(e) => setRangeEnd(e.target.value)}
                  />
                </label>
              </div>
              <div className="tm-actions">
                <button
                  type="button"
                  className="tm-apply"
                  onClick={applyRange}
                >
                  Apply
                </button>
                <button
                  type="button"
                  className="tm-cancel"
                  onClick={() => setOpenMenu(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Selected summary */}
      <div className="tm-summary">
        <strong>Selected:</strong> {getSummaryText()}
      </div>
    </div>
  );
}
